import type React from 'react';
import type { SyntheticEvent } from 'react';

import { AttentionTriangleIcon, DetectionIcon, ArrowInCircleRightIcon } from '@vlabs/icons';
import { useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type {
  AttributeSamples, FaceCandidateMatchResult, FaceCandidateMatchResultFace, FaceId, SampleId,
} from '@vlabs/api/luna-api/model';
import { routes } from '@vlabs/shared/navigation/routes';
import { FaceSample, Tag, Control } from '@vlabs/uikit';
import i18next from 'i18next';
import { generatePath } from 'react-router-dom';

import st from './DuplicatedFaceConfirmation.module.sass';

const formatSimilarityValue = (similarity: number): string => {
  return `${Math.round(similarity * 100)} %`;
};

interface DuplicatedFaceConfirmationProps {
  faceSrc?: string;
  onConfirm: VoidFunction;
  onReject: VoidFunction;
  match: FaceCandidateMatchResult;
}

function DuplicatedFaceConfirmation({
  faceSrc = undefined,
  onConfirm,
  onReject,
  match,
}: DuplicatedFaceConfirmationProps): React.JSX.Element | null {
  const getFaceAttributeSamples = useGetFaceAttributeSamples(
    (match.face as FaceCandidateMatchResultFace).face_id as FaceId,
  );
  const faceSampleId: SampleId | undefined = (getFaceAttributeSamples.data as AttributeSamples)?.samples[0];

  const handleClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    // @ts-expect-error не типизирован routes
    const link = generatePath(routes.facePage, { faceId: face.face_id });
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={st.DuplicatedFaceConfirmation}>
      <div className={st.Title}>
        <AttentionTriangleIcon />
        <div>{i18next.t('card-list-page:features.face duplicate.confirm popup.title')}</div>
      </div>
      <div className={st.Description}>
        {i18next.t('card-list-page:features.face duplicate.confirm popup.description')}
        <div>{match?.face?.external_id}</div>
      </div>
      <div className={st.Samples}>
        <FaceSample
          size="large"
          src={faceSrc}
        />
        <FaceSample
          buttonOnBottom={(
            <Control.CircleAnimatedButton
              icon={<ArrowInCircleRightIcon />}
              kind="primary"
              onClick={handleClick}
              size="small"
            >
              {i18next.t('card-list-page:features.face duplicate.confirm popup.buttons.face sample button')}
            </Control.CircleAnimatedButton>
          )}
          size="large"
          src={faceSampleId}
        />
        {!!match.similarity && (
          <Tag
            className={st.Tag}
            icon={<DetectionIcon />}
            value={formatSimilarityValue(match.similarity)}
          />
        )}
      </div>
      <div className={st.Buttons}>
        <Control.Button
          kind="attention"
          onClick={onConfirm}
          variant="outlined"
        >
          {i18next.t('card-list-page:features.face duplicate.confirm popup.buttons.submit button')}
        </Control.Button>
        <Control.Button
          kind="secondary"
          onClick={onReject}
          variant="outlined"
        >
          {i18next.t('card-list-page:features.face duplicate.confirm popup.buttons.reject button')}
        </Control.Button>
      </div>
    </div>
  );
}

export { DuplicatedFaceConfirmation };
